import React, { useMemo } from 'react';
import { ArrowSquareOut, Eye, UserCircle } from '@phosphor-icons/react';

import { StatusBadge } from './status-badge';
import { FormattedMessage } from '../markdown';
import { useConvosContext } from './convos';
import { FeedbackDialog, DiscardReason } from './feedback-dialog';
import { DivButton } from '../ui/div-button';
import { Button } from '../ui/button';
import { ConfidenceBadge } from './confidence-badge';

import { GeneratingStar } from '@/components/generating-star';
import { Item, ItemHeader, ItemContent, ItemTitle } from '@/components/ui/item';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/formatDate';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import './message.css';

// Preserve markdown code and GFM angle-bracket autolinks before HTML escaping.
const PROTECTED_MARKDOWN_SEGMENT =
  /(```[\s\S]*?```|`[^`\n]+`|<(?:[a-zA-Z][a-zA-Z0-9+.-]*:|www\.)[^\s>]+>)/g;

const GFM_ANGLE_BRACKET_AUTOLINK = /^<(?:[a-zA-Z][a-zA-Z0-9+.-]*:|www\.)[^\s>]+>$/;

function isProtectedMarkdownSegment(segment: string) {
  return (
    segment.startsWith('```') ||
    (segment.startsWith('`') && segment.endsWith('`')) ||
    GFM_ANGLE_BRACKET_AUTOLINK.test(segment)
  );
}

function escapeHtmlEntities(text: string) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeHtmlOutsideProtectedMarkdownSegments(content: string) {
  return content
    .split(PROTECTED_MARKDOWN_SEGMENT)
    .map((segment) => (isProtectedMarkdownSegment(segment) ? segment : escapeHtmlEntities(segment)))
    .join('');
}

export interface RelatedArticle {
  id: string;
  title: string;
  url: string;
  score: number;
}

export enum MessageType {
  ACTION = 'action', // user action like 'Next Best Action clicks'
  FEEDBACK = 'feedback', // feedback for up/down vote
  QUESTION = 'question', // user question
  ANSWER = 'answer', // ai generated answer
}

export enum Feedback {
  Positive = 'positive',
  Negative = 'negative',
}

// Shared base fields
interface BaseMessage {
  id: string;
  timestamp: Date;
  locale?: string; // only if not using en-US. For formatting dates (overrides context locale)
  isFeedbackDialogOpen?: boolean; // controlled state for feedback dialog
  onFeedbackDialogChange?: (isOpen: boolean) => void; // callback for feedback dialog state changes
}

// AI Answer - has related articles, discard info, confidence
export interface AIAnswerMessage extends BaseMessage {
  type: MessageType.ANSWER;
  role: 'ai';
  content: string;
  relatedArticles?: RelatedArticle[];
  discarded?: boolean;
  discardedMeta?: {
    reason: DiscardReason;
    suggestedAnswer?: string;
  };
  confidenceScore?: number;
}

export interface Attachment {
  type: 'image' | string;
  url: string;
}

// User Question
export interface UserQuestionMessage extends BaseMessage {
  type: MessageType.QUESTION;
  role: 'user';
  content: string;
  attachments?: Attachment[];
  onlyShowAttachments?: boolean;
}

// User Feedback - requires feedback field
export interface UserFeedbackMessage extends BaseMessage {
  type: MessageType.FEEDBACK;
  role: 'user';
  content: string; // optional comment text
  feedback: Feedback;
}

// User Action
export interface UserActionMessage extends BaseMessage {
  type: MessageType.ACTION;
  role: 'user';
  content: string;
}

// Union type
export type MessageProps = AIAnswerMessage | UserQuestionMessage | UserFeedbackMessage | UserActionMessage;

// Type guards for external use
export const isAIAnswer = (msg: MessageProps): msg is AIAnswerMessage => msg.type === MessageType.ANSWER;

export const isUserQuestion = (msg: MessageProps): msg is UserQuestionMessage => msg.type === MessageType.QUESTION;

export const isFeedback = (msg: MessageProps): msg is UserFeedbackMessage => msg.type === MessageType.FEEDBACK;

export const isAction = (msg: MessageProps): msg is UserActionMessage => msg.type === MessageType.ACTION;

function MessageAttachmentImageThumbnails({ attachments }: { attachments: Attachment[] }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {attachments
        .filter((a) => a.type === 'image')
        .map((att, i) => (
          <div key={i} className="relative bg-dark-300 p-1 rounded shrink-0">
            <a href={att.url} target="_blank" rel="noopener noreferrer">
              <img src={att.url} alt="" className="size-14 object-cover rounded" />
            </a>
            <div className="absolute top-1.5 right-1.5 bg-dark-300 rounded-full p-0.5">
              <Eye size={8} />
            </div>
          </div>
        ))}
    </div>
  );
}

function Message(props: MessageProps) {
  const { id, content, timestamp, locale: localeOverride, isFeedbackDialogOpen, onFeedbackDialogChange } = props;

  const purifiedContent = useMemo(() => escapeHtmlOutsideProtectedMarkdownSegments(content), [content]);

  // Get locale from context, with override if provided
  const { locale: contextLocale } = useConvosContext();
  const locale = localeOverride || contextLocale;

  const formattedDate = formatDate(timestamp, locale);

  if (isAIAnswer(props)) {
    const { relatedArticles, discarded, discardedMeta, confidenceScore } = props;

    return (
      <li className="relative before-line after-line list-none [&_a]:break-all">
        <Item className="message-item">
          <ItemHeader className="justify-start gap-0">
            <GeneratingStar variant="gradient" className="relative -left-[6px]" />
            <ItemTitle className="text-xs font-bold">{`Generated answer ${discarded ? '(Discarded)' : ''}`}</ItemTitle>
          </ItemHeader>
          <ItemContent
            className={cn('message-ai-content', {
              'py-6': !relatedArticles?.length,
              'pt-6 pb-0': relatedArticles?.length,
            })}
          >
            <div className="message-content-wrapper">
              <FormattedMessage message={{ content: purifiedContent }} />
              <div className="flex gap-2 justify-between mt-2 items-center">
                <span className="message-timestamp">{formattedDate}</span>
                <div className="flex items-center gap-2">
                  {confidenceScore && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DivButton variant="outline" size="sm" className="text-xs p-1 h-auto">
                          {confidenceScore}
                        </DivButton>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Answer score: {confidenceScore}</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  <FeedbackDialog
                    id={id}
                    discarded={discarded}
                    discardedMeta={discardedMeta}
                    open={isFeedbackDialogOpen}
                    onOpenChange={onFeedbackDialogChange}
                  />
                </div>
              </div>
            </div>
            {!!relatedArticles?.length && (
              <Accordion type="single" collapsible className="p-0 custom-dashed-border-t-dark-300 w-full">
                <AccordionItem value="related-articles">
                  <AccordionTrigger className="px-6">Related articles</AccordionTrigger>
                  <AccordionContent>
                    <ul className="message-article-list">
                      {relatedArticles.map((article) => (
                        <li
                          key={article.id}
                          className="grid gap-2 grid-cols-[auto_1fr] items-center justify-items-start"
                        >
                          <ConfidenceBadge score={Math.round(article.score * 100)} />
                          <Button
                            variant="link"
                            size="xs"
                            asChild
                            className="flex items-center gap-2 min-w-0 !justify-start w-full"
                          >
                            <a href={article.url} target="_blank" rel="noopener noreferrer">
                              <span className="truncate">{article.title}</span>
                              <ArrowSquareOut className="size-4 flex-shrink-0" weight="regular" />
                            </a>
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </ItemContent>
        </Item>
      </li>
    );
  }

  // User messages (Question, Feedback, Action)
  const feedback = isFeedback(props) ? props.feedback : null;

  return (
    <li className="relative list-none before-line--user after-line--user">
      <Item className="message-item">
        <ItemHeader className="basis-0">
          <UserCircle weight="fill" size={24} />
        </ItemHeader>
        <ItemContent className="message-user-content">
          {isUserQuestion(props) && (
            <div className="flex flex-col gap-2">
              {props.attachments && props.attachments.some((a) => a.type === 'image') && (
                <MessageAttachmentImageThumbnails attachments={props.attachments} />
              )}
              {!props.onlyShowAttachments && <FormattedMessage message={{ content: purifiedContent }} />}
              <span className="message-timestamp">{formattedDate}</span>
            </div>
          )}
          {isFeedback(props) && feedback && (
            <>
              <div className="message-header">
                <div className="message-feedback-text">
                  {feedback === Feedback.Positive ? 'Upvoted as helpful' : 'Downvoted as not helpful'}
                </div>
                <div className="message-meta">
                  <StatusBadge variant={feedback === Feedback.Positive ? 'success' : 'destructive'}>
                    {feedback === Feedback.Positive ? 'Answered' : 'Unable to help'}
                  </StatusBadge>
                </div>
              </div>
              {content && <div className="text-base mb-1">{content}</div>}
              <span className="message-timestamp-simple">{formattedDate}</span>
            </>
          )}
          {isAction(props) && (
            <div className="message-header">
              <div className="flex flex-col gap-2">
                <span className="text-subtle text-sm italic">{content}</span>
                <span className="message-timestamp-simple">{formattedDate}</span>
              </div>

              <div className="message-meta">
                <StatusBadge variant="warning">Action</StatusBadge>
              </div>
            </div>
          )}
        </ItemContent>
      </Item>
    </li>
  );
}

export { Message, MessageAttachmentImageThumbnails };
